import { ALGOS_E } from "@constants/Algos";
import { RecordResponse } from "@services/http/responses/BaseResponse";
import { ChartValueEvent } from "@services/http/socket/responses";
import { dev } from "@utils/dev";
import { time } from "@utils/moscowTime";
import {
  ChartData,
  ChartDataset,
  ChartOptions,
  ScriptableContext,
  Tick,
  TimeUnit,
  TooltipItem,
  TooltipModel,
} from "chart.js";
import "chartjs-adapter-moment";
import moment, { Moment, isMoment } from "moment-timezone";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { IS_DEBUG } from "../../config";
import ChartColors from "./ChartColors";
import ChartDatasetUtils from "./ChartDatasetUtils";
import { MomentPeriod } from "./CleanChart.control";

export const FIVE_MIN_IN_MS = 300000; /* 5*60*1000 */
export const TO_MS_FACTOR = 24 * 60 * 60 * 1000;

export interface IChartOptionMethods {
  lastTickValue: number | null;

  getBackgroundColor: (value: number) => ChartColors;
  getTooltipTitle: (value: number) => string;
  getTooltipLabel(item: TooltipItem<"line">, algos: ALGOS_E): string;
  // onGetElementPointStyle: () => PointStyle,
  // getTooltipYAlign: (item?: TooltipItem<"line">) => "top" | "center" | "bottom";
  getYTick(value: number): string;
  getXTick: (index: any, periodDays: number) => string;

  onLastTick: (last: Tick | null) => void;
}

export function calcSpeedDrops(
  speedDrops?: number,
  defaultSpeed?: number
): number {
  return ((speedDrops ?? 0) * (defaultSpeed ?? 0)) / 100;
}

export function isHasData(values: number[]): boolean {
  if (values.length === 0) return false;
  for (let i = 0; i < values.length; i++) {
    if (!isNaN(values[i])) {
      return true;
    }
  }
  return false;
}

export function currentColor(
  hash: number,
  nextHash: number,
  speedDrops?: number,
  defaultSpeed?: number
): ChartColors {
  // 0..speedDrops => OFF
  // speedDrops..speedDefault => LOW
  // speedDefault - 5%.. => SECONDARY

  if (defaultSpeed === undefined || speedDrops === undefined) {
    return ChartColors.PRIMARY;
  }
  const defaultSpeedReal = defaultSpeed * 0.95;
  if (hash < nextHash) {
    // up
    if (nextHash <= speedDrops) {
      return ChartColors.OFF;
    }
    if (hash >= speedDrops && nextHash <= defaultSpeedReal) {
      return ChartColors.LOW;
    }
    return ChartColors.SECONDARY;
  }
  if (hash > nextHash) {
    // down
    if (hash <= defaultSpeedReal && nextHash >= speedDrops) {
      return ChartColors.LOW;
    }
    if (hash <= speedDrops) {
      return ChartColors.OFF;
    }
    if (nextHash <= speedDrops) {
      return ChartColors.OFF;
    }
    return ChartColors.SECONDARY;
  }
  if (hash <= speedDrops) {
    return ChartColors.OFF;
  }
  if (hash <= defaultSpeedReal) {
    return ChartColors.LOW;
  }
  return ChartColors.SECONDARY;
}
interface ChartDatasetParams {
  data: number[];
  speedDrops?: number;
  defaultSpeed?: number;
  label?: string | undefined;
}

export function createChartDataset({
  data,
  speedDrops,
  defaultSpeed,
  label,
}: ChartDatasetParams): ChartDataset<"line"> {
  return {
    label,
    data,
    borderWidth: 2,
    fill: true,
    tension: 0.4,
    borderColor: IS_DEBUG
      ? ChartColors.BG_PRIMARY
      : defaultSpeed === undefined
        ? ChartColors.BG_PRIMARY
        : undefined,
    backgroundColor(context: ScriptableContext<"line">) {
      const { ctx } = context.chart;
      const gradient = ctx.createLinearGradient(0, 0, 0, 600);
      gradient.addColorStop(0, ChartColors.BG_PRIMARY);
      gradient.addColorStop(0.8, "rgba(255, 255, 255, 0)");
      return gradient;
    },
    segment: {
      backgroundColor(ctx, options) {
        if (defaultSpeed === undefined || speedDrops === undefined) {
          return undefined;
        }
        const hash = ctx.p0.parsed.y ?? 0;
        const nextHash = ctx.p1.parsed.y ?? 0;
        return currentColor(hash, nextHash, speedDrops, defaultSpeed);
      },
    },
  };
}

export function createChartOptions(
  showLegends: boolean,
  showY1Scales: boolean,
  methods: IChartOptionMethods,
  onAnimationEnd: () => void,
  periodDays: number,
  hashAlgo = ALGOS_E.SHA256
): ChartOptions<"line"> {
  return {
    spanGaps: true,
    normalized: true,
    responsive: true,
    maintainAspectRatio: false,
    //devicePixelRatio: 1,
    animation: {
      delay: 0,
      duration: 0,
      onComplete(e) {
        onAnimationEnd();
      },
    },
    hover: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      // zoom: {
      //   zoom: {
      //     wheel: {
      //       enabled: true,
      //     },
      //     pinch: {
      //       enabled: true
      //     },
      //     mode: 'x',
      //   }
      // },

      legend: {
        display: IS_DEBUG ? showLegends : false,
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      datalabels: {
        display(context: any) {
          return false;
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        displayColors: false,
        position: "average",
        // yAlign(ctx: any) {
        //   return methods.getTooltipYAlign(ctx.tooltipItems[0]);
        // },
        itemSort(a: any, b: any, data: any): number {
          if (a.datasetIndex === 0) return -1;
          if (b.datasetIndex === 0) return -1;
          if (a.datasetIndex === 1) return 1;
          if (b.datasetIndex === 1) return 1;
          return 0;
        },
        callbacks: {
          title(
            this: TooltipModel<"line">,
            tooltipItems: TooltipItem<"line">[]
          ) {
            if (this.dataPoints.length > 0) {
              return methods.getTooltipTitle(this.dataPoints[0].parsed.x ?? 0);
            }
            return "";
          },

          label(
            this: TooltipModel<"line">,
            item: TooltipItem<"line">
          ): string | string[] {
            return methods.getTooltipLabel(item, hashAlgo);
          },
        },
      },
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 6,
        hoverBorderColor: ChartColors.BG_PRIMARY,
        hoverBackgroundColor(ctx, options) {
          return methods.getBackgroundColor(ctx.raw as number);
        },
      },
    },
    scales: {
      x: {
        afterUpdate(axis) {
          let lastTick = null;
          if (axis.ticks.length > 0) {
            lastTick = { ...axis.ticks[axis.ticks.length - 1] };
          }
          methods.onLastTick(lastTick);
        },
        grid: {
          display: false,
        },
        type: "time",
        time: {
          unit: getTimeUnit(),
        },
        ticks: {
          major: {
            enabled: true,
          },
          autoSkip: periodDays > 1,
          maxTicksLimit: 40,
          callback: (value, index, ticks) => {
            return methods.getXTick(ticks[index].value, periodDays);
          },
        },
      },
      y: {
        // grace: 1,
        beginAtZero: true,
        grid: {
          display: false,
        },
        position: "left",
        ticks: {
          callback: (value, index, values) => {
            if (typeof value === "string") {
              return methods.getYTick(parseFloat(value));
            }
            return methods.getYTick(value);
          },
        },
      },
      y1: {
        type: "linear",
        beginAtZero: true,
        display: showY1Scales,
        position: "right",
        grid: {
          display: false,
        },
        max: 100,
        title: {
          display: true,
          font: {
            size: 15,
          },
        },
      },
    },
  };
  function getTimeUnit(): TimeUnit {
    if (periodDays < 3) return "minute";
    if (periodDays < 15) return "hour";
    return "day";
  }
}

function isNonZeroArray(value: number[]): boolean {
  for (let i = 0; i < value.length; i++) {
    if (value[i] !== 0 && !isNaN(value[i])) {
      return true;
    }
  }
  return false;
}

function isChangeCurrentDay(
  eventDate: Moment,
  period: [Moment, Moment]
): boolean {
  if (period[1].isSame(eventDate, "day")) {
    return true;
  }
  return false;
}

function getLastTime(chart: ChartJSOrUndefined<"line"> | null): number {
  if (
    chart &&
    chart.data &&
    chart.data.labels &&
    chart.data.labels.length > 0
  ) {
    return chart.data.labels[chart.data.labels.length - 1] as number;
  }
  return new Date().getTime();
}

function isEmpty(chart: ChartJSOrUndefined<"line"> | null): boolean {
  if (
    chart &&
    chart.data &&
    chart.data.datasets &&
    chart.data.datasets.length > 0
  ) {
    for (let i = 0; i < chart.data.datasets[0].data.length; i++) {
      if (
        chart.data.datasets[0].data[i] !== 0 &&
        !isNaN(chart.data.datasets[0].data[i] as number)
      ) {
        return false;
      }
    }
  }
  return true;
}

function isNonZeroChart(chart: ChartJSOrUndefined<"line"> | null): boolean {
  if (
    chart &&
    chart.data &&
    chart.data.datasets &&
    chart.data.datasets.length > 0
  ) {
    return isNonZeroArray(chart.data.datasets[0].data as number[]);
  }
  return false;
}

function isAnotherDay(
  chart: ChartJSOrUndefined<"line"> | null,
  e: ChartValueEvent
): boolean {
  if (chart && chart.data.labels && chart.data.labels.length > 0) {
    const lastChartDay = moment(
      chart.data.labels[chart.data.labels.length - 1] as number
    );
    //new Date(
    //   chart.data.labels[chart.data.labels.length - 1] as number
    // ).getDate();
    // if (new Date(e.time * 1000).getDate() !== lastChartDay) {
    return moment(
      e.time * 1000 + window.SERVER_TIME_DELTA ? window.SERVER_TIME_DELTA : 0
    ).isSame(lastChartDay, "day");
  }
  return false;
}

function getCoords(chart: ChartJSOrUndefined<"line"> | null): [number, number] {
  if (chart) {
    const lastData = chart.getDatasetMeta(0).data;
    if (lastData.length > 0) {
      return [lastData[lastData.length - 1].x, lastData[lastData.length - 1].y];
    }
  }
  return [0, 0];
}

function getLastPoint(
  chart: ChartJSOrUndefined<"line"> | null
): [number, number, number] {
  if (chart) {
    const labels = chart.data.labels;
    const datasets = chart.data.datasets;
    if (labels && datasets && datasets.length && datasets.length > 0) {
      if (datasets[0].data && datasets[0].data.length > 0) {
        const value = datasets[0].data[datasets[0].data.length - 1] as number;
        let rejectRate = -1;
        if (datasets.length > 1) {
          rejectRate = datasets[1].data[datasets[1].data.length - 1] as number;
        }
        return [getLastTime(chart), value, rejectRate];
      }
    }
  }
  return [-1, -1, -1];
}

function isTooLate(
  chart: ChartJSOrUndefined<"line"> | null,
  timeInMs: number
): boolean {
  // TODO maybe: для разных периодов разный таймаут?
  const lastTime = getLastTime(chart) + 25 * 60 * 1000;
  return lastTime <= timeInMs; //e.time;
}

function isSingleDay(period: [Moment, Moment]): boolean {
  return period[1].diff(period[0], "days") === 1;
}

function isTupleOfMoment(date: any): date is [Moment, Moment] {
  return (
    Array.isArray(date) &&
    date.length === 2 &&
    date.every((item) => isMoment(item))
  );
}

function fmt(date: Moment | [Moment, Moment]): string {
  const fmt = "DD.MM.YYYY HH:mm:ss";
  if (isMoment(date)) {
    return date.format(fmt);
  }
  if (isTupleOfMoment(date)) {
    return `[${date[0].format(fmt)}, ${date[1].format(fmt)}]`;
  }
  return "";
}

function isAllowAdd(unixTimeSec: number, period: [Moment, Moment]): boolean {
  //const delta = window.SERVER_TIME_DELTA ? window.SERVER_TIME_DELTA : 0;
  const eventDate = time(unixTimeSec);
  //dev.log(`isAllowAdd ${eventDate.format("DD.MM.yyyy HH:mm:ss")} ${period[0].format("DD.MM.yyyy HH:mm:ss")} ${period[1].format("DD.MM.yyyy HH:mm:ss")}`);
  if (isSingleDay(period)) {
    //dev.log("Single day", period);
    if (eventDate.isSameOrAfter(period[1], "second")) {
      // dev.force(`Single day, result: false, event date: ${fmt(eventDate)}, period: ${fmt(period)}`);
      return false;
    }
    const result = eventDate.isSameOrAfter(period[0], "second");
    //dev.force(`Single day, result: ${result}, event date: ${fmt(eventDate)}, period: ${fmt(period)}`);
    return result;
  }
  const isBetween = eventDate.isBetween(
    period[1].clone().subtract(1).startOf("day"),
    period[1],
    "second",
    "[)"
  );
  // dev.force(`Not a single day, result: ${isBetween}, event date: ${fmt(eventDate)}, period: ${fmt(period)}`);
  return isBetween;
}

function createPeriodForRequest(from: Moment, until?: Moment): MomentPeriod {
  if (!until) {
    return [
      from.clone().startOf("day"),
      from.clone().add(1, "day").startOf("day"),
    ];
  }
  return [
    from.clone().startOf("day"),
    until.clone().add(1, "day").startOf("day"),
  ];
}

function showStatisticsDebug(
  message: string,
  response: RecordResponse<number[][]> | void
) {
  if (response && response.Record) {
    // for debug only
    const [times, hashes] = response.Record;
    if (times.length > 0) {
      dev.log(message, {
        from: moment(times[0] * 1000).format("DD.MM.YYYY HH:mm:ss"),
        to: moment(times[times.length - 1] * 1000).format(
          "DD.MM.YYYY HH:mm:ss"
        ),
        timesCount: times.length,
        hashesCount: hashes.length,
      });
    } else {
      dev.log(message, {});
    }
  }
}

// function createEmptyLabelsFor(
//   until: Moment
// ): string[] {
//   const result: string[] = [];
//   const start = until.clone().startOf("day");
//   const deltaInMins = 5;
//   for (let current = start; current.isSameOrBefore(until); current.add(deltaInMins, "minutes")) {
//     result.push(current.format("DD.MM.YYYY HH:mm:ss"));
//   }
//   return result;
// }

function createEmptyLabelsForPeriod(period: [Moment, Moment]): number[] {
  const result: number[] = [];
  //const start = until.clone().startOf("day");
  const start = period[0].clone();
  const end = period[1].isAfter(moment()) ? moment() : period[1].clone();
  const deltaInMins = ChartDatasetUtils.getDiscreteByPeriodInMins(period); //getPeriodForToday;
  for (
    let current = start;
    current.isSameOrBefore(end);
    current.add(deltaInMins, "minutes")
  ) {
    result.push(current.unix() * 1000);
  }
  return result;
}

function getChartData(
  chart: ChartJSOrUndefined<"line"> | null
): ChartData<"line", number[], number> | null {
  if (!chart || !chart.data) {
    dev.force("No chart component");
    return null;
  }
  const { datasets, labels } = chart.data;
  if (!datasets || !labels) {
    dev.force("No datasets or labels");
    return null;
  }
  return { datasets, labels } as ChartData<"line", number[], number>;
}

const ChartUtils = {
  isChangeCurrentDay,
  getLastPoint,
  getCoords,
  isAnotherDay,
  isEmpty,
  getLastTime,
  isNonZeroArray,
  isSingleDay,
  isAllowAdd,
  isTooLate,
  createPeriodForRequest,
  isNonZeroChart,
  createEmptyLabelsForPeriod,
  getChartData,
  ///
  showStatisticsDebug,
};

export default ChartUtils;
